import { useState } from 'react'

interface YouTubeVideoProps {
  /** The YouTube video id (the part after youtu.be/) */
  videoId: string
  title: string
  /** Our own poster in /public, so nothing loads from YouTube before a click */
  poster?: string
}

/**
 * A YouTube video that shows only our poster until it is clicked. Then the
 * privacy-enhanced youtube-nocookie player loads and starts playing, so a
 * visitor who never presses play never talks to YouTube.
 */
export function YouTubeVideo({ videoId, title, poster }: YouTubeVideoProps) {
  const [playing, setPlaying] = useState(false)

  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-lg border border-gray-800 bg-black">
      {playing ? (
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
          className="absolute inset-0 h-full w-full"
        />
      ) : (
        <button
          type="button"
          onClick={() => setPlaying(true)}
          aria-label={`Play ${title}`}
          className="group absolute inset-0 h-full w-full"
        >
          {poster ? (
            <img src={poster} alt="" loading="lazy" className="h-full w-full object-cover" />
          ) : (
            <span className="flex h-full w-full items-end bg-gradient-to-br from-gray-900 to-black p-4 text-left text-sm font-medium text-gray-300">
              {title}
            </span>
          )}
          <span className="absolute inset-0 flex items-center justify-center bg-black/20 transition-colors group-hover:bg-black/10">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-black/70 ring-1 ring-white/30 transition-transform group-hover:scale-110">
              <svg viewBox="0 0 24 24" className="ml-1 h-7 w-7 fill-white" aria-hidden="true">
                <path d="M8 5v14l11-7z" />
              </svg>
            </span>
          </span>
        </button>
      )}
    </div>
  )
}
